import { useState } from 'react';
import { FiPhone, FiMail, FiMapPin, FiClock, FiArrowRight } from 'react-icons/fi';
import SectionHeading from './SectionHeading';
import useReveal from './useReveal';

const contactRows = [
  { icon: FiPhone, label: 'Phone', value: '+251 927 727 196', href: 'tel:+251927727196' },
  { icon: FiPhone, label: 'Alternative phone', value: '+251 922 313 333', href: 'tel:+251922313333' },
  { icon: FiMail, label: 'Email', value: 'info@minatechnologies.com', href: 'mailto:info@minatechnologies.com' },
  { icon: FiMapPin, label: 'Visit', value: 'Kaliti, Addis Ababa' },
  { icon: FiClock, label: 'Response time', value: 'Within 24 hours' },
];

const fields = [
  { key: 'name', label: 'Your name', placeholder: 'John Doe', type: 'text' },
  { key: 'phone', label: 'Phone number (optional)', placeholder: '0912345678', type: 'tel' },
];


const CONTACT_EMAIL = 'info@minatechnologies.com';

function Contactus() {
  const [formData, setFormData] = useState({ name: '', phone: '', message: '' });
  const [errors, setErrors] = useState({});
  const [sent, setSent] = useState(false);
  const revealRef = useReveal();

  const validateForm = () => {
    const nextErrors = {};

    if (!formData.name.trim()) {
      nextErrors.name = 'Name is required';
    } else if (!/^[\p{L}\s.'-]+$/u.test(formData.name.trim())) {
      nextErrors.name = 'Name can only contain letters and spaces';
    }

    if (formData.phone && !/^\+?[\d\s-]{7,}$/.test(formData.phone.trim())) {
      nextErrors.phone = 'Enter a valid phone number';
    }

    if (formData.message.trim().length < 10) {
      nextErrors.message = 'Tell us a little more (at least 10 characters)';
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  // Opens the visitor's email app with the message ready to send.
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const name = formData.name.trim();
    const subject = `Project inquiry from ${name}`;
    const body = [
      formData.message.trim(),
      '',
      '---',
      `Name: ${name}`,
      formData.phone ? `Phone: ${formData.phone.trim()}` : null,
    ]
      .filter((line) => line !== null)
      .join('\n');

    window.location.href = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    setSent(true);
  };

  const update = (key) => (e) => {
    setFormData({ ...formData, [key]: e.target.value });
    setSent(false);
  };

  return (
    <div ref={revealRef} className="section-pad bg-surface">
      <div className="wrap">
        <SectionHeading
          center
          eyebrow="Contact"
          title={<>Let's build something <span className="text-gradient">great together</span></>}
          lede="Have a project in mind or questions about our services? Drop us a message and we'll get back to you within 24 hours."
        />

        <div className="mt-14 grid gap-5 lg:grid-cols-12">
          <aside className="reveal glow-dark rounded-card p-8 lg:col-span-5 lg:p-10" data-reveal>
            <h3 className="display-md text-white">Talk to our team</h3>
            <p className="mt-3 text-subtle">Tell us what you're building. We'll reply with next steps and a clear estimate.</p>
            <ul className="mt-8 divide-y divide-white/10">
              {contactRows.map(({ icon: Icon, label, value, href }) => (
                <li key={value} className="flex items-center gap-4 py-4">
                  <span className="icon-tile h-10 w-10">
                    <Icon size={18} />
                  </span>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.14em] text-subtle">{label}</p>
                    {href ? (
                      <a href={href} className="font-medium text-white hover:text-cyan">{value}</a>
                    ) : (
                      <p className="font-medium text-white">{value}</p>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </aside>

          <form
            onSubmit={handleSubmit}
            noValidate
            className="reveal card p-8 lg:col-span-7 lg:p-10"
            data-reveal
            style={{ '--d': '80ms' }}
          >
            <div className="grid gap-5 sm:grid-cols-2">
              {fields.map((field) => (
                <div key={field.key}>
                  <label htmlFor={`contact-${field.key}`} className="mb-2 block text-sm font-semibold text-ink">
                    {field.label}
                  </label>
                  <input
                    id={`contact-${field.key}`}
                    type={field.type}
                    className="field"
                    placeholder={field.placeholder}
                    value={formData[field.key]}
                    onChange={update(field.key)}
                  />
                  {errors[field.key] && <p className="mt-1.5 text-sm text-rose-600">{errors[field.key]}</p>}
                </div>
              ))}

              <div className="sm:col-span-2">
                <label htmlFor="contact-message" className="mb-2 block text-sm font-semibold text-ink">
                  Your message
                </label>
                <textarea
                  id="contact-message"
                  className="field min-h-[150px] resize-y"
                  placeholder="Tell us about your project..."
                  value={formData.message}
                  onChange={update('message')}
                />
                {errors.message && <p className="mt-1.5 text-sm text-rose-600">{errors.message}</p>}
              </div>
            </div>

            <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:items-center">
              <button type="submit" className="btn-primary group w-full sm:w-auto">
                Send message <FiArrowRight className="transition-transform group-hover:translate-x-0.5" />
              </button>
              <p className="text-sm text-muted">Opens your email app with the message ready to send.</p>
            </div>

            {sent && (
              <p className="fade-in mt-6 rounded-xl border border-brand/20 bg-brand/5 px-4 py-3 text-sm text-ink" role="status">
                Your email app should now be open — just press <strong>Send</strong>. Nothing happened? Email us
                directly at{' '}
                <a href={`mailto:${CONTACT_EMAIL}`} className="font-semibold text-brand hover:underline">
                  {CONTACT_EMAIL}
                </a>
                .
              </p>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}

export default Contactus;
